import { FC, useCallback, useEffect, useState } from 'react';

import cn from 'classnames';
import { MdNavigateBefore, MdNavigateNext } from 'react-icons/md';
import { RadioGroup } from '@headlessui/react';
import { useFormikContext } from 'formik';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { DateField } from 'ui/components/form';
import { Button } from 'ui/components/common';
import {
  useGetPublicBookingEventAvailabilityDate,
  useGetPublicBookingEventAvailabilityTime,
} from 'modules/account/calendar/hooks';
import convertTime from 'modules/account/calendar/utils/convertTime';

import { PUBLIC_BOOKING_DETAILS } from './ClientPublicBooking';
import { CLIENT_PUBLIC_BOOKING_FORM } from '../../forms/schema';

type PROPS = {
  publicBookingDetails: PUBLIC_BOOKING_DETAILS;
};

const NoTimeAvailable: FC = () => (
  <div className="flex items-center w-full h-full">
    <div
      className={cn(
        'text-disabled',
        'font-normal',
        'text-sm',
        'text-center',
        'border',
        'border-separator-secondary',
        'rounded-md',
        'p-16',
      )}
    >
      We can’t find available time on that date.
    </div>
  </div>
);

const ClientPublicBookingForm: FC<PROPS> = ({ publicBookingDetails }) => {
  const { id } = useParams();
  const { values, handleSubmit, isSubmitting, setFieldValue } =
    useFormikContext<CLIENT_PUBLIC_BOOKING_FORM>();
  const _selectedDate = dayjs(values.selectedDate);
  const [selectedMonth, setSelectedMonth] = useState<string>(
    `${dayjs().month() + 1}`,
  );
  const [selectedYear, setSelectedYear] = useState(
    dayjs().format('YYYY').toLowerCase(),
  );

  const { data: dates, isLoading: availableDatesLoading } =
    useGetPublicBookingEventAvailabilityDate(
      id as string,
      selectedMonth,
      selectedYear,
    );

  const { data: times } = useGetPublicBookingEventAvailabilityTime(
    id as string,
    _selectedDate.format('YYYY-MM-DD'),
  );

  const availableDates: Date[] = dates?.dates?.map(
    (date: string) => new Date(date),
  );
  const filterDate = useCallback(
    (date: Date) => {
      return availableDates?.some((availableDate) =>
        dayjs(availableDate).isSame(dayjs(date), 'day'),
      );
    },
    [availableDates],
  );

  useEffect(() => {
    if (times?.times && times?.times.length) {
      if (!values.startAt) {
        setFieldValue('startAt', times?.times[0]);
      }
    }
  }, [setFieldValue, times?.times, values.startAt]);

  useEffect(() => {
    if (availableDates?.length) {
      if (!values.selectedDate) {
        setFieldValue('selectedDate', availableDates[0]);
      }
    }
  }, [availableDates, setFieldValue, values.selectedDate]);

  return (
    <form
      onSubmit={handleSubmit}
      className="shadow-leapify-sm max-w-[700px] space-y-4 rounded-[6px] bg-fill-tertiary p-8"
    >
      <div
        className={cn(
          'text-[22px]',
          'font-bold',
          'leading-7',
          'tracking-[0.88px]',
          'pb-4',
          'border-b-[0.5px]',
          'border-b-separator-primary',
        )}
      >
        Booking Invite
      </div>
      <div className="space-y-4 border-b-[0.5px] border-separator-primary pb-4">
        <p className="text-sm font-bold leading-[22px] tracking-[0.56px] text-info-base">
          Meeting Details
        </p>
        <div className="grid grid-cols-2 gap-x-5 gap-y-4">
          <div className="col-span-1">
            <p className="text-sm font-semibold text-content">Booking Title</p>
            <p className="text-sm font-bold text-secondary-dark">
              {publicBookingDetails?.title}
            </p>
          </div>
          <div className="col-span-1">
            <p className="text-sm font-semibold text-content">
              Booking Meeting Duration
            </p>
            <p className="text-sm font-bold text-secondary-dark">
              {publicBookingDetails?.duration} Minutes
            </p>
          </div>
          <div className="col-span-2">
            <p className="text-sm font-semibold text-content">Description</p>
            <p className="text-sm font-bold text-secondary-dark">
              {publicBookingDetails?.content}
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-4 border-b-[0.5px] border-separator-primary pb-4">
        <div className="text-sm font-bold leading-[22px] tracking-[0.56px] text-info-base">
          Attendee Details
        </div>
        <div className="grid grid-cols-2 gap-x-5 gap-y-4">
          <div className="col-span-1">
            <p className="text-sm font-semibold text-content">Guest Name</p>
            <p className="text-sm font-bold text-secondary-dark">
              {publicBookingDetails?.guest?.fullName}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
          <div>
            <div
              className={cn(
                'font-bold',
                'leading-[22px]',
                'tracking-[0.56px]',
                'text-sm',
                'mb-[10px]',
              )}
            >
              Select a Date & Time
            </div>
            <DateField
              name="selectedDate"
              selected={new Date(values.selectedDate as string)}
              inline
              filterDate={filterDate}
              disabled={availableDatesLoading}
              onMonthChange={(month) => {
                setSelectedMonth(dayjs(month).format('MM'));
                setSelectedYear(dayjs(month).format('YYYY'));
              }}
              onChange={(name, d) =>
                setFieldValue(
                  name as string,
                  dayjs(d as Date)
                    .startOf('day')
                    .toDate(),
                )
              }
              renderCustomHeader={({
                date,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }) => (
                <div className="flex items-center justify-between border-separator-primary py-4">
                  <p className="text-sm font-semibold">
                    {date.toLocaleString('default', {
                      month: 'long',
                    })}{' '}
                    {date.getFullYear()}
                  </p>
                  <div className="flex items-center gap-x-5">
                    <button
                      type="button"
                      onClick={decreaseMonth}
                      disabled={prevMonthButtonDisabled}
                      className="text-content"
                    >
                      <MdNavigateBefore className="text-lg" />
                    </button>
                    <button
                      type="button"
                      className={cn(
                        'text-primary-base disabled:bg-transparent disabled:text-disabled',
                        'transition',
                      )}
                      onClick={increaseMonth}
                      disabled={nextMonthButtonDisabled}
                    >
                      <MdNavigateNext className="text-lg" />
                    </button>
                  </div>
                </div>
              )}
            />
          </div>
          <div className="flex-auto">
            <div
              className={cn(
                'font-bold',
                'leading-[22px]',
                'tracking-[0.56px]',
                'text-sm',
                'mb-[10px]',
              )}
            >
              {_selectedDate.isValid() &&
                _selectedDate.format('dddd, MMMM D YYYY')}
            </div>
            {!times?.times?.length ? (
              <NoTimeAvailable />
            ) : (
              <RadioGroup
                defaultValue={values?.startAt}
                defaultChecked={!!values?.startAt as boolean}
                value={values?.startAt}
                onChange={(value) => {
                  setFieldValue('startAt', value);
                }}
              >
                <RadioGroup.Label className="sr-only">
                  Available Times
                </RadioGroup.Label>
                <div
                  className={cn(
                    'flex',
                    'flex-col',
                    'self-stretch',
                    'gap-[6px]',
                    'max-h-[calc(42px*7)]',
                    'overflow-y-auto',
                    'px-1',
                  )}
                >
                  {times?.times?.map((time: string) => (
                    <RadioGroup.Option
                      key={time}
                      value={time}
                      className="rounded-md"
                    >
                      {({ active, checked }) => (
                        <Button
                          size={'md'}
                          intent={active || checked ? 'primary' : 'tertiary'}
                          label={convertTime(time)}
                          className={cn('w-full')}
                        />
                      )}
                    </RadioGroup.Option>
                  ))}
                </div>
              </RadioGroup>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-end justify-end ">
        <Button
          disabled={isSubmitting || !values?.selectedDate || !values?.startAt}
          type="submit"
          label="NEXT"
          className="font-bold"
        />
      </div>
    </form>
  );
};

export default ClientPublicBookingForm;
